/** Options controlling the arrowhead shape. */
export interface ArrowHeadOptions {
    /** Arrowhead length in scene units. Default scales with stroke width: max(16, 4·width). */
    headLength?: number;
    /** Half-angle of the arrowhead in radians. Default 30° (Math.PI / 6). */
    headAngle?: number;
}

/**
 * Build an SVG path string for an arrow from (x1,y1) to (x2,y2): a straight shaft
 * plus an open (stroked, non-filled) V-shaped head at the (x2,y2) tip.
 *
 * Pure geometry — no fabric dependency — so it's trivially testable and reusable.
 * Use the result with `new Path(buildArrowPath(...), {...})`.
 */
export function buildArrowPath(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    strokeWidth = 4,
    options?: ArrowHeadOptions
): string {
    const headLength = options?.headLength ?? Math.max(16, strokeWidth * 4);
    const headAngle = options?.headAngle ?? Math.PI / 6;

    const angle = Math.atan2(y2 - y1, x2 - x1);

    const leftX = x2 - headLength * Math.cos(angle - headAngle);
    const leftY = y2 - headLength * Math.sin(angle - headAngle);
    const rightX = x2 - headLength * Math.cos(angle + headAngle);
    const rightY = y2 - headLength * Math.sin(angle + headAngle);

    const r = (n: number) => Math.round(n * 100) / 100;

    // Shaft, then the two head strokes meeting at the tip.
    return (
        `M ${r(x1)} ${r(y1)} L ${r(x2)} ${r(y2)} ` +
        `M ${r(leftX)} ${r(leftY)} L ${r(x2)} ${r(y2)} L ${r(rightX)} ${r(rightY)}`
    );
}
